import { useEffect, useRef, useState } from 'react';
import Icon from '../ui/Icon';
import FieldError from '../ui/FieldError';
import { ApiError } from '../../api/client';
import { requestPasswordReset } from '../../api/auth';
import { validateEmail } from '../../utils/validation';
import { AlertBox, inputBase, inputInvalid, inputOk, pillButton } from './AuthShell';

/** Step 1: ask for the email and send the 6-digit code. */
export default function EmailStep({ initialEmail = '', onSent }) {
  const [email, setEmail] = useState(initialEmail);
  const [emailError, setEmailError] = useState();
  const [submitted, setSubmitted] = useState(false);
  const [serverError, setServerError] = useState(null); // { message, canRetry }
  const [sending, setSending] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => inputRef.current?.focus(), []);

  const send = async () => {
    if (sending) return;
    setSubmitted(true);
    setServerError(null);
    const error = validateEmail(email);
    setEmailError(error);
    if (error) {
      inputRef.current?.focus();
      return;
    }
    setSending(true);
    const trimmed = email.trim();
    try {
      const result = await requestPasswordReset(trimmed);
      // Same answer whether or not the account exists, so always continue to the code step
      onSent({ email: trimmed, ...result });
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Something went wrong. Please try again.';
      if (err?.status === 400) {
        setEmailError(message);
        inputRef.current?.focus();
      } else {
        // 503 = the email couldn't be sent; anything else is a server/network problem
        setServerError({ message, canRetry: true });
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <h1 className="text-2xl font-bold text-on-surface text-center mb-2.5">Forgot Password?</h1>
      <p className="text-[13.5px] leading-relaxed text-on-surface-variant text-center px-1 mb-6">
        Enter your email address and we will send an OTP to verify your account.
      </p>

      <form
        noValidate
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          send();
        }}
      >
        {serverError && (
          <AlertBox
            action={
              serverError.canRetry && (
                <button type="button" onClick={send} disabled={sending} className="font-bold underline underline-offset-2 disabled:opacity-60">
                  Try again
                </button>
              )
            }
          >
            {serverError.message}
          </AlertBox>
        )}

        <div>
          <label htmlFor="forgot-email" className="block text-sm font-bold text-on-surface mb-1.5">
            Email <span className="text-error">*</span>
          </label>
          <input
            ref={inputRef}
            id="forgot-email"
            name="email"
            type="email"
            autoComplete="email"
            placeholder="name@example.com"
            value={email}
            disabled={sending}
            onChange={(e) => {
              setEmail(e.target.value);
              setServerError(null);
              if (submitted) setEmailError(validateEmail(e.target.value));
            }}
            aria-invalid={Boolean(emailError)}
            aria-describedby={emailError ? 'forgot-email-error' : undefined}
            className={`${inputBase} ${emailError ? inputInvalid : inputOk}`}
          />
          <FieldError id="forgot-email-error">{emailError}</FieldError>
        </div>

        <div className="pt-1">
          <button type="submit" disabled={sending} className={pillButton}>
            {sending ? (
              <>
                <Icon name="progress_activity" size={20} className="animate-spin" />
                Sending…
              </>
            ) : (
              <>
                Send OTP
                <Icon name="arrow_forward" size={20} />
              </>
            )}
          </button>
        </div>
      </form>
    </>
  );
}
